// UI Component: Advanced 3D Tilt Card with Framer Motion
import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import "./TiltCard.css";

const springValues = {
  damping: 30,
  stiffness: 100,
  mass: 2,
};

const TiltCard = ({ 
  children, 
  variant = 'base',
  rotateAmplitude = 12,
  scaleOnHover = 1.02,
  className = '',
  onClick,
  colorOverlay = null,
  shadowColor = null,
  containerHeight = "auto",
  containerWidth = "100%",
  showTooltip = false,
  tooltipText = "",
  ...props 
}) => {
  const ref = useRef(null);

  const x = useMotionValue();
  const y = useMotionValue();
  const rotateX = useSpring(useMotionValue(0), springValues);
  const rotateY = useSpring(useMotionValue(0), springValues);
  const scale = useSpring(1, springValues);
  const opacity = useSpring(0);

  const [lastY, setLastY] = useState(0);

  const variants = {
    base: {
      background: 'rgba(255, 255, 255, 0.9)',
      borderRadius: 'var(--radius-3xl)',
      padding: 'var(--spacing-2xl)',
      boxShadow: 'var(--shadow-sm)',
      border: '1px solid rgba(148, 163, 184, 0.1)',
      backdropFilter: 'blur(10px)'
    },
    floating: {
      background: 'rgba(255, 255, 255, 0.95)',
      borderRadius: 'var(--radius-4xl)',
      padding: 'var(--spacing-3xl)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid rgba(148, 163, 184, 0.08)',
      backdropFilter: 'blur(20px)'
    },
    feature: {
      background: 'rgba(255, 255, 255, 0.95)',
      borderRadius: 'var(--radius-3xl)',
      padding: 'var(--spacing-2xl)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid rgba(148, 163, 184, 0.08)',
      position: 'relative',
      overflow: 'hidden',
      backdropFilter: 'blur(15px)'
    }
  };

  const cardStyles = {
    ...variants[variant],
    ...(colorOverlay && {
      background: `linear-gradient(135deg, ${colorOverlay}08, rgba(255,255,255,0.95))`,
      backdropFilter: 'blur(15px)'
    }),
    height: containerHeight,
    width: containerWidth,
    padding: '0', // Figure'da padding olmasın
  };

  function handleMouse(e) {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - rect.width / 2;
    const offsetY = e.clientY - rect.top - rect.height / 2;

    const rotationX = (offsetY / (rect.height / 2)) * -rotateAmplitude;
    const rotationY = (offsetX / (rect.width / 2)) * rotateAmplitude;

    rotateX.set(rotationX);
    rotateY.set(rotationY);

    if (showTooltip) {
      x.set(e.clientX - rect.left);
      y.set(e.clientY - rect.top);
    }

    const velocityY = offsetY - lastY;
    setLastY(offsetY);
  }

  function handleMouseEnter() {
    scale.set(scaleOnHover);
    if (showTooltip) {
      opacity.set(1);
    }
  }

  function handleMouseLeave() {
    opacity.set(0);
    scale.set(1);
    rotateX.set(0);
    rotateY.set(0);
  }

  // Shadow color için data attribute belirleme
  const getColorAttribute = () => {
    if (shadowColor) return shadowColor;
    if (colorOverlay) {
      if (colorOverlay.includes('20, 184, 166')) return 'teal';
      if (colorOverlay.includes('251, 146, 60')) return 'orange';
      if (colorOverlay.includes('147, 51, 234')) return 'purple';
      if (colorOverlay.includes('59, 130, 246')) return 'blue';
    }
    return null;
  };

  return (
    <figure
      ref={ref}
      className={`tilted-card-figure ${variant === 'floating' ? 'variant-floating' : ''} ${className}`}
      style={cardStyles}
      data-color={getColorAttribute()}
      onMouseMove={handleMouse}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      {...props}
    >
      <motion.div
        className="tilted-card-inner"
        style={{
          rotateX,
          rotateY,
          scale,
          width: '100%',
          height: '100%',
          borderRadius: variant === 'floating' ? 'var(--radius-4xl)' : 'var(--radius-3xl)',
          overflow: 'hidden',
          padding: variants[variant].padding,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </motion.div>

      {showTooltip && tooltipText && (
        <motion.figcaption
          className="tilted-card-caption"
          style={{
            x,
            y,
            opacity,
          }}
        >
          {tooltipText}
        </motion.figcaption>
      )}
    </figure>
  );
};

export default TiltCard; 